// TwoFactorSetup.tsx - Configuración de 2FA para profesionales
import { useState, useEffect } from 'react';

interface TwoFactorSetupProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const TwoFactorSetup: React.FC<TwoFactorSetupProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const [step, setStep] = useState<'intro' | 'qr' | 'verify' | 'backup' | 'complete'>('intro');
  const [qrCode, setQrCode] = useState('');
  const [manualCode, setManualCode] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showManualCode, setShowManualCode] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  // Reset cuando se cierra
  useEffect(() => {
    if (!isOpen) {
      setStep('intro');
      setQrCode('');
      setManualCode('');
      setVerificationCode('');
      setBackupCodes([]);
      setError('');
      setShowManualCode(false);
    }
  }, [isOpen]);

  // Iniciar setup - obtener QR
  const handleStartSetup = async () => {
    setIsLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/auth/2fa/setup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setQrCode(data.qrCode);
        setManualCode(data.manualEntry);
        setStep('qr');
      } else {
        setError(data.error || 'Error iniciando configuración');
      }
    } catch (err) {
      console.error('Error en setup 2FA:', err);
      setError('Error de conexión');
    } finally {
      setIsLoading(false);
    }
  };

  // Verificar código y activar 2FA
  const handleVerify = async () => {
    if (verificationCode.length !== 6) {
      setError('Ingresa el código de 6 dígitos');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/auth/2fa/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ token: verificationCode })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setBackupCodes(data.backupCodes);
        setStep('backup');
      } else {
        setError(data.error || 'Código incorrecto');
        setVerificationCode('');
      }
    } catch (err) {
      console.error('Error verificando 2FA:', err);
      setError('Error de conexión');
    } finally {
      setIsLoading(false);
    }
  };

  // Copiar códigos de respaldo
  const handleCopyBackupCodes = () => {
    const codesText = backupCodes.join('\n');
    navigator.clipboard.writeText(codesText);
    alert('Códigos copiados al portapapeles');
  };

  // Descargar códigos como archivo
  const handleDownloadBackupCodes = () => {
    const codesText = `Códigos de respaldo ConsultAR\n${'='.repeat(30)}\n\n${backupCodes.join('\n')}\n\n⚠️ Guarda estos códigos en un lugar seguro.\nCada código solo puede usarse una vez.`;
    const blob = new Blob([codesText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'consultar-backup-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Finalizar
  const handleComplete = () => {
    setStep('complete');
    setTimeout(() => {
      onSuccess();
      onClose();
    }, 2000);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full p-8 max-h-[90vh] overflow-y-auto">
        
        {/* PASO 1: Introducción */}
        {step === 'intro' && (
          <>
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-green-500 rounded-full flex items-center justify-center">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-center text-gray-900 mb-4">
              Protege tu cuenta
            </h2>

            <p className="text-gray-600 text-center mb-6">
              La verificación en dos pasos agrega una capa extra de seguridad. 
              Necesitarás tu contraseña y un código de tu teléfono para iniciar sesión.
            </p>

            <div className="bg-blue-50 rounded-xl p-4 mb-6">
              <h3 className="font-semibold text-blue-900 mb-2">Vas a necesitar:</h3>
              <ul className="text-blue-800 text-sm space-y-2">
                <li className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Una app de autenticación (Google Authenticator, Authy, etc.)
                </li>
                <li className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  2 minutos de tu tiempo
                </li>
              </ul>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl">
                <p className="text-red-600 text-sm text-center">{error}</p>
              </div>
            )}

            <button
              onClick={handleStartSetup}
              disabled={isLoading}
              className="w-full py-4 bg-gradient-to-r from-blue-500 to-green-500 text-white rounded-xl font-semibold text-lg hover:shadow-lg hover:scale-105 transition-all duration-300 disabled:opacity-50"
            >
              {isLoading ? 'Iniciando...' : 'Comenzar configuración'}
            </button>

            <button
              onClick={onClose}
              className="w-full mt-4 py-3 text-gray-500 hover:text-gray-700"
            >
              Cancelar
            </button>
          </>
        )}

        {/* PASO 2: Mostrar QR */}
        {step === 'qr' && (
          <>
            <h2 className="text-2xl font-bold text-center text-gray-900 mb-2">
              Escanea el código QR
            </h2>
            <p className="text-gray-600 text-center mb-6">
              Abre tu app de autenticación y escanea este código
            </p>

            <div className="flex justify-center mb-6">
              <div className="p-4 bg-white rounded-xl shadow-lg border-2 border-gray-100">
                <img src={qrCode} alt="QR Code" className="w-48 h-48" />
              </div>
            </div>

            <button
              onClick={() => setShowManualCode(!showManualCode)}
              className="w-full text-blue-600 hover:text-blue-800 text-sm mb-4"
            >
              {showManualCode ? 'Ocultar código manual' : '¿No puedes escanear? Ingresa el código manualmente'}
            </button>

            {showManualCode && (
              <div className="bg-gray-100 rounded-xl p-4 mb-6">
                <p className="text-xs text-gray-500 mb-2">Código manual:</p>
                <p className="font-mono text-sm break-all select-all bg-white p-2 rounded border">
                  {manualCode}
                </p>
              </div>
            )}

            <button
              onClick={() => setStep('verify')}
              className="w-full py-4 bg-gradient-to-r from-blue-500 to-green-500 text-white rounded-xl font-semibold text-lg hover:shadow-lg transition-all duration-300"
            >
              Ya escaneé el código
            </button>

            <button
              onClick={onClose}
              className="w-full mt-4 py-3 text-gray-500 hover:text-gray-700"
            >
              Cancelar
            </button>
          </>
        )}

        {/* PASO 3: Verificar código */}
        {step === 'verify' && (
          <>
            <h2 className="text-2xl font-bold text-center text-gray-900 mb-2">
              Verifica tu configuración
            </h2>
            <p className="text-gray-600 text-center mb-6">
              Ingresa el código de 6 dígitos que muestra tu app
            </p>

            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={verificationCode}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '');
                setVerificationCode(value);
                setError('');
              }}
              placeholder="000000"
              className="w-full p-4 text-center text-3xl font-mono tracking-widest border-2 border-gray-200 rounded-xl mb-4 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
            />

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl">
                <p className="text-red-600 text-sm text-center">{error}</p>
              </div>
            )}

            <button
              onClick={handleVerify}
              disabled={verificationCode.length !== 6 || isLoading}
              className="w-full py-4 bg-gradient-to-r from-blue-500 to-green-500 text-white rounded-xl font-semibold text-lg hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Verificando...' : 'Verificar y activar'}
            </button>

            <button
              onClick={() => setStep('qr')}
              className="w-full mt-4 py-3 text-gray-500 hover:text-gray-700"
            >
              ← Volver al QR
            </button>
          </>
        )}

        {/* PASO 4: Códigos de respaldo */}
        {step === 'backup' && (
          <>
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-center text-gray-900 mb-2">
              ¡2FA Activado!
            </h2>
            <p className="text-gray-600 text-center mb-4">
              Guarda estos códigos de respaldo en un lugar seguro
            </p>

            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-4">
              <p className="text-yellow-800 text-sm">
                ⚠️ Estos códigos te permitirán acceder si pierdes tu teléfono. 
                Cada código solo puede usarse una vez.
              </p>
            </div>

            <div className="bg-gray-100 rounded-xl p-4 mb-4">
              <div className="grid grid-cols-2 gap-2">
                {backupCodes.map((code, index) => (
                  <div key={index} className="font-mono text-sm bg-white p-2 rounded text-center">
                    {code}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-2 mb-6">
              <button
                onClick={handleCopyBackupCodes}
                className="flex-1 py-3 bg-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-300 transition-colors"
              >
                📋 Copiar
              </button>
              <button
                onClick={handleDownloadBackupCodes}
                className="flex-1 py-3 bg-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-300 transition-colors"
              >
                💾 Descargar
              </button>
            </div>

            <button
              onClick={handleComplete}
              className="w-full py-4 bg-gradient-to-r from-blue-500 to-green-500 text-white rounded-xl font-semibold text-lg hover:shadow-lg transition-all duration-300"
            >
              Ya guardé mis códigos
            </button>
          </>
        )}

        {/* PASO 5: Completado */}
        {step === 'complete' && (
          <div className="text-center py-8">
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center animate-bounce">
                <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              ¡Todo listo!
            </h2>
            <p className="text-gray-600">
              Tu cuenta ahora está protegida con verificación en dos pasos
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TwoFactorSetup;